import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtHours } from "@/lib/api";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { PageHeader, BookingDetailDialog, Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const SPANS = [["1w", "1 week"], ["2w", "2 weeks"], ["4w", "4 weeks"], ["month", "1 month"]];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mondayOf = (d) => { const day = (d.getDay() + 6) % 7; return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day); };
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export default function TeamCalendar() {
  const [sp] = useSearchParams();
  const [cursor, setCursor] = useState(() => {
    const d = sp.get("date");
    if (d) { const parsed = new Date(d); if (!isNaN(parsed.getTime())) return parsed; }
    return new Date();
  });
  const [view, setView] = useState("grid"); // grid = week-style table, month = avatar grid
  const [span, setSpan] = useState("2w");
  const [data, setData] = useState({ bookings: [], bank_holidays: [], employees: [], schedule: {} });
  const [allEmployees, setAllEmployees] = useState([]);
  const [dept, setDept] = useState("all");
  const [manager, setManager] = useState("all");
  const [detail, setDetail] = useState(null);
  const { map: ltMap } = useLeaveTypes();

  useEffect(() => { api.get("/employees").then((r) => setAllEmployees(r.data)).catch(() => {}); }, []);

  const y = cursor.getFullYear(), m = cursor.getMonth();
  const monthStart = new Date(y, m, 1);
  const monthEnd = new Date(y, m + 1, 0);

  // Grid (table) range + visible weekday columns
  let gridStart, gridEnd;
  if (span === "month") { gridStart = monthStart; gridEnd = monthEnd; }
  else { const w = span === "1w" ? 1 : span === "2w" ? 2 : 4; gridStart = mondayOf(cursor); gridEnd = addDays(gridStart, w * 7 - 1); }
  const gridDays = [];
  for (let d = new Date(gridStart); d <= gridEnd; d = addDays(d, 1)) {
    const wd = (d.getDay() + 6) % 7;
    if (wd < 5) gridDays.push(new Date(d)); // weekdays only, same as 1-week format
  }

  const rangeStart = view === "grid" ? gridStart : monthStart;
  const rangeEnd = view === "grid" ? gridEnd : monthEnd;

  const departments = [...new Set(allEmployees.map((e) => e.department).filter(Boolean))];
  const managers = allEmployees.filter((e) => allEmployees.some((x) => x.manager_id === e.id));

  useEffect(() => {
    const params = { start: iso(rangeStart), end: iso(rangeEnd) };
    if (dept !== "all") params.department = dept;
    if (manager !== "all") params.manager_id = manager;
    api.get("/calendar", { params }).then((r) => setData(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso(rangeStart), iso(rangeEnd), dept, manager]);

  const bhOn = (dIso) => data.bank_holidays.find((b) => b.date === dIso);
  const appliesOn = (b, dIso) => {
    if (!(dIso >= b.start_date && dIso <= b.end_date)) return false;
    const row = (b.breakdown || []).find((r) => r.date === dIso);
    if (!row) return true;                              // no detail → show across the whole span
    if ((row.hours_deducted ?? 0) > 0) return true;     // any day that actually used leave (incl. overrides)
    if (row.is_non_working) return false;               // genuine weekend / non-working day within the span
    return (row.scheduled_hours ?? 0) > 0;
  };
  const bookingFor = (empId, dIso) => data.bookings.find((b) => b.employee_id === empId && appliesOn(b, dIso));
  const bookingsOnDay = (dIso) => data.bookings.filter((b) => appliesOn(b, dIso));
  const hoursOn = (b, dIso) => {
    const row = (b.breakdown || []).find((r) => r.date === dIso);
    return row ? (row.hours_deducted ?? row.scheduled_hours ?? 0) : b.total_hours;
  };

  const step = (dir) => {
    if (view === "month" || span === "month") setCursor(new Date(y, m + dir, 1));
    else { const w = span === "1w" ? 1 : span === "2w" ? 2 : 4; setCursor(addDays(cursor, dir * w * 7)); }
  };

  // Coverage across visible grid days
  const dayStats = gridDays.map((d) => {
    const di = iso(d);
    let working = 0, available = 0;
    data.employees.forEach((e) => {
      const sc = data.schedule[e.id]?.[di];
      if (sc && sc > 0) {
        working++;
        const off = data.bookings.some((b) => b.employee_id === e.id && b.status === "approved"
          && di >= b.start_date && di <= b.end_date
          && (b.breakdown || []).some((r) => r.date === di && (r.hours_deducted ?? 0) > 0));
        if (!off) available++;
      }
    });
    return { d, di, working, available };
  });
  const lowest = dayStats.filter((s) => s.working > 0).sort((a, b) => a.available - b.available)[0];

  // Avatar month grid cells
  const firstDow = (monthStart.getDay() + 6) % 7;
  const monthCells = [];
  for (let i = 0; i < firstDow; i++) monthCells.push(null);
  for (let d = 1; d <= monthEnd.getDate(); d++) monthCells.push(new Date(y, m, d));

  const rangeLabel = view === "month"
    ? `${MONTHS[m]} ${y}`
    : span === "month"
      ? `${MONTHS[m]} ${y}`
      : span === "1w"
        ? `Week commencing ${gridStart.getDate()} ${MONTHS[gridStart.getMonth()]} ${gridStart.getFullYear()}`
        : `${gridStart.getDate()} ${SHORT[gridStart.getMonth()]} – ${gridEnd.getDate()} ${SHORT[gridEnd.getMonth()]} ${gridEnd.getFullYear()}`;

  return (
    <div className="space-y-5">
      <PageHeader title="Team calendar" subtitle="Coverage across the team">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="cal-view-toggle">
            {[["grid", "Grid"], ["month", "Month"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${view === v ? "bg-[#2EA3FD] text-white font-semibold" : "text-slate-600 hover:text-[#0B1A2F]"}`}>
                {l}
              </button>
            ))}
          </div>
          {view === "grid" && (
            <Select value={span} onValueChange={setSpan}>
              <SelectTrigger className="w-32 bg-white h-9" data-testid="cal-span-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SPANS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="w-32 bg-white h-9" data-testid="cal-dept-filter"><SelectValue placeholder="Team" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All teams</SelectItem>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={manager} onValueChange={setManager}>
            <SelectTrigger className="w-32 bg-white h-9" data-testid="cal-manager-filter"><SelectValue placeholder="Manager" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All managers</SelectItem>{managers.map((mg) => <SelectItem key={mg.id} value={mg.id}>{mg.first_name} {mg.last_name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => step(-1)} data-testid="cal-prev"><ChevronLeft size={15} className="mr-1" />Previous</Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" onClick={() => step(1)} data-testid="cal-next">Next<ChevronRight size={15} className="ml-1" /></Button>
        </div>
      </PageHeader>

      <p className="text-sm text-slate-500 -mt-2" data-testid="cal-range-label">{rangeLabel}</p>

      {view === "grid" ? (
        <>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto cass-scroll">
            <table className="w-full border-collapse table-fixed" style={{ minWidth: Math.max(760, 224 + gridDays.length * 96) }}>
              <thead>
                <tr className="bg-slate-50 text-xs font-semibold text-slate-600">
                  <th className="text-left px-4 py-3 sticky left-0 bg-slate-50 z-10" style={{ width: 224 }}>Employee</th>
                  {gridDays.map((d) => {
                    const bh = bhOn(iso(d));
                    return (
                      <th key={iso(d)} className="px-2 py-3 text-center border-l border-slate-100" style={{ width: 96 }}>
                        <div>{DOW[(d.getDay() + 6) % 7]} {d.getDate()}{gridDays.length > 5 ? ` ${SHORT[d.getMonth()]}` : ""}</div>
                        {bh && <div className="text-[10px] font-medium text-indigo-600 truncate" title={bh.name} data-testid={`cal-bh-${iso(d)}`}>{bh.name}</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.employees.length === 0 && <tr><td colSpan={gridDays.length + 1} className="text-center text-slate-400 py-10">No employees to show</td></tr>}
                {data.employees.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`cal-row-${e.id}`}>
                    <td className="px-4 py-2 sticky left-0 bg-white z-10">
                      <span className="inline-flex items-center gap-2.5 min-w-0">
                        <Avatar name={e.name} photo={e.photo_url} size={30} />
                        <span className="min-w-0"><span className="block text-sm font-medium text-[#0B1A2F] truncate">{e.name}</span>{e.department && <span className="block text-[11px] text-slate-400 truncate">{e.department}</span>}</span>
                      </span>
                    </td>
                    {gridDays.map((d) => {
                      const di = iso(d);
                      const b = bookingFor(e.id, di);
                      const sc = data.schedule[e.id]?.[di];
                      const bh = bhOn(di);
                      const nonWorking = !b && sc === 0;
                      return (
                        <td key={di} className={`px-1.5 py-1.5 align-top border-l border-slate-100 ${bh ? "bg-indigo-50/40" : nonWorking ? "bg-slate-200/70" : ""}`} data-testid={`cal-cell-${e.id}-${di}`}>
                          {b ? (() => {
                            const approved = b.status === "approved";
                            const color = ltMap[b.leave_type]?.color || "#2EA3FD";
                            const name = ltMap[b.leave_type]?.name || b.leave_type;
                            return (
                              <button onClick={() => setDetail(b)} data-testid={`cal-booking-${b.id}`}
                                style={{ backgroundColor: (approved ? color : "#F59E0B") + "1F", borderLeftColor: approved ? color : "#F59E0B" }}
                                className="w-full text-left rounded-md border-l-4 px-2 py-1 hover:brightness-95 transition-all"
                                title={`${e.name} · ${name} · ${hoursOn(b, di)}h${approved ? "" : " (pending)"}`}>
                                <p className="text-[11px] font-semibold leading-tight truncate" style={{ color: approved ? color : "#B45309" }}>{approved ? name : "Pending"}</p>
                                <p className="text-[10px] text-slate-500 leading-tight">{hoursOn(b, di)}h</p>
                              </button>
                            );
                          })() : null}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl bg-slate-100/80 border border-slate-200 p-4" data-testid="coverage-banner">
            <p className="text-sm font-semibold text-[#0B1A2F] mb-2">Coverage {span === "1w" ? "this week" : "for this period"}</p>
            {gridDays.length <= 14 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {dayStats.map((s) => (
                  <span key={s.di} className={`text-xs px-2.5 py-1 rounded-full border ${s.working > 0 && s.available === lowest?.available ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-white border-slate-200 text-slate-600"}`}>
                    {DOW[(s.d.getDay() + 6) % 7]} {s.d.getDate()} · <span className="font-semibold">{s.available}/{s.working}</span> in
                  </span>
                ))}
              </div>
            )}
            <p className="text-sm text-slate-600">
              {lowest && lowest.working > 0
                ? <>Lowest cover is <span className="font-semibold">{DOW[(lowest.d.getDay() + 6) % 7]} {lowest.d.getDate()} {SHORT[lowest.d.getMonth()]}</span> with <span className="font-semibold">{lowest.available} of {lowest.working}</span> scheduled staff available.</>
                : "No scheduled working staff in this period for the current filter."}
            </p>
          </div>
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="cal-month-grid">
          <div className="grid grid-cols-7 bg-slate-50 text-xs font-semibold text-slate-600">
            {DOW.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d, i) => {
              const di = d && iso(d);
              const bh = d && bhOn(di);
              const isWeekend = d && (d.getDay() === 0 || d.getDay() === 6);
              return (
                <div key={i}
                  style={bh ? { backgroundColor: (ltMap["bank_holiday"]?.color || "#6366F1") + "1A" } : undefined}
                  className={`min-h-24 border-b border-r border-slate-100 p-1 ${!d ? "bg-slate-50/50" : isWeekend ? "bg-slate-200/70" : ""}`}
                  data-testid={d ? `cal-day-${di}` : undefined}>
                  {d && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-400">{d.getDate()}</span>
                        {bh && <span className="text-[9px] bg-indigo-100 text-indigo-700 px-1 rounded">BH</span>}
                      </div>
                      {bh && <div className="text-[9px] px-1 py-0.5 rounded truncate bg-indigo-50 text-indigo-700 font-medium mb-1" title={bh.name} data-testid={`cal-bh-${di}`}>{bh.name}</div>}
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {bookingsOnDay(di).map((b) => {
                          const approved = b.status === "approved";
                          const color = approved ? (ltMap[b.leave_type]?.color || "#10b981") : "#F59E0B";
                          return (
                            <button key={b.id} onClick={() => setDetail(b)} data-testid={`cal-booking-${b.id}`}
                              className="rounded-full hover:ring-2 hover:ring-[#0B1A2F]/30 hover:ring-offset-1 transition-all cursor-pointer"
                              title={`${b.employee_name} · ${ltMap[b.leave_type]?.name || b.leave_type} · ${fmtHours(b.total_hours)}${approved ? "" : " (pending)"}`}>
                              <Avatar name={b.employee_name} photo={b.employee_photo} color={color} size={24} />
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 text-xs text-slate-500 items-center">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-l-4 border-l-[#2EA3FD] bg-sky-100" />Approved (leave-type colour)</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm border-l-4 border-l-amber-500 bg-amber-100" />Pending</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-slate-200 border border-slate-300" />Non-working / weekend</span>
        <span className="text-slate-400">· Click a leave block/avatar for full details</span>
      </div>

      <BookingDetailDialog booking={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} />
    </div>
  );
}
